import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Form, Button, Table,
  Modal, Badge, Alert, Spinner, InputGroup, ButtonGroup, Dropdown
} from 'react-bootstrap';
import axios from 'axios';
import AdminNavbar from './components/AdminNavbar';
import { BASE_URL } from '../../services/api';

const QuickStatsManager = () => {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingStat, setEditingStat] = useState(null);
  const [deletingStat, setDeletingStat] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    value: '',
    icon: 'fas fa-chart-line',
    color: 'primary',
    link: '',
    display_order: 0,
    is_active: true
  });

  const API_BASE_URL = BASE_URL;

  const colorOptions = [
    { value: 'primary', label: 'Blue', class: 'bg-primary' },
    { value: 'secondary', label: 'Gray', class: 'bg-secondary' },
    { value: 'success', label: 'Green', class: 'bg-success' },
    { value: 'danger', label: 'Red', class: 'bg-danger' },
    { value: 'warning', label: 'Yellow', class: 'bg-warning' },
    { value: 'info', label: 'Cyan', class: 'bg-info' },
    { value: 'dark', label: 'Dark', class: 'bg-dark' }
  ];

  const iconOptions = [
    'fas fa-users', 'fas fa-cogs', 'fas fa-project-diagram', 'fas fa-award',
    'fas fa-smile', 'fas fa-user-tie', 'fas fa-trophy', 'fas fa-globe',
    'fas fa-chart-line', 'fas fa-star', 'fas fa-heart', 'fas fa-flag',
    'fas fa-building', 'fas fa-home', 'fas fa-briefcase', 'fas fa-graduation-cap'
  ];

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE_URL}/quick_stats.php?admin=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setStats(res.data.data);
      } else {
        setError(res.data.message || 'Failed to load quick stats');
      }
    } catch (err) {
      console.error('Fetch stats error:', err);
      setError('Failed to load quick stats. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      value: '',
      icon: 'fas fa-chart-line',
      color: 'primary',
      link: '',
      display_order: 0,
      is_active: true
    });
    setEditingStat(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleEdit = (stat) => {
    setEditingStat(stat);
    setFormData({
      title: stat.title || '',
      value: stat.value || '',
      icon: stat.icon || 'fas fa-chart-line',
      color: stat.color || 'primary',
      link: stat.link || '',
      display_order: stat.display_order || 0,
      is_active: Boolean(stat.is_active)
    });
    setShowModal(true);
  };

  const handleDeleteClick = (stat) => {
    setDeletingStat(stat);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!deletingStat) return;
    
    try {
      setError('');
      const token = localStorage.getItem('token');
      const res = await axios.delete(`${API_BASE_URL}/quick_stats.php?id=${deletingStat.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.success) {
        setSuccess('Quick stat deleted successfully');
        fetchStats();
        setShowDeleteModal(false);
        setDeletingStat(null);
      } else {
        setError(res.data.message || 'Failed to delete quick stat');
      }
    } catch (err) {
      console.error('Delete error:', err);
      setError('Failed to delete quick stat. Please try again.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.title.trim() || !formData.value.trim()) {
      setError('Title and Value are required');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      let res;

      if (editingStat) {
        res = await axios.put(`${API_BASE_URL}/quick_stats.php?id=${editingStat.id}`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      } else {
        res = await axios.post(`${API_BASE_URL}/quick_stats.php`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }

      if (res.data.success) {
        setSuccess(editingStat ? 'Quick stat updated successfully' : 'Quick stat added successfully');
        setShowModal(false);
        resetForm();
        fetchStats();
      } else {
        setError(res.data.message || (editingStat ? 'Failed to update quick stat' : 'Failed to add quick stat'));
      }
    } catch (err) {
      console.error('Save error:', err);
      setError('An error occurred. Please try again.');
    }
  };

  const toggleStatus = async (stat) => {
    try {
      setError('');
      const token = localStorage.getItem('token');
      const updatedData = {
        ...stat,
        is_active: !Boolean(stat.is_active)
      };
      
      const res = await axios.put(`${API_BASE_URL}/quick_stats.php?id=${stat.id}`, updatedData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (res.data.success) {
        setSuccess(`Quick stat ${stat.is_active ? 'deactivated' : 'activated'} successfully`);
        fetchStats();
      } else {
        setError(res.data.message || 'Failed to update status');
      }
    } catch (err) {
      console.error('Toggle status error:', err);
      setError('Failed to update status. Please try again.');
    }
  };

  const moveOrder = async (stat, direction) => {
    try {
      setError('');
      const token = localStorage.getItem('token');
      
      // Find current position and calculate new position
      const currentIndex = stats.findIndex(s => s.id === stat.id);
      let newOrder;
      
      if (direction === 'up' && currentIndex > 0) {
        // Swap with previous item
        newOrder = stats[currentIndex - 1].display_order;
        stats[currentIndex - 1].display_order = stat.display_order;
      } else if (direction === 'down' && currentIndex < stats.length - 1) {
        // Swap with next item
        newOrder = stats[currentIndex + 1].display_order;
        stats[currentIndex + 1].display_order = stat.display_order;
      } else {
        return; // Can't move further
      }
      
      // Update the stat's order
      const updatedData = {
        ...stat,
        display_order: newOrder
      };
      
      const res = await axios.put(`${API_BASE_URL}/quick_stats.php?id=${stat.id}`, updatedData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (res.data.success) {
        setSuccess('Order updated successfully');
        fetchStats();
      } else {
        setError(res.data.message || 'Failed to update order');
      }
    } catch (err) {
      console.error('Move order error:', err);
      setError('Failed to update order. Please try again.');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  if (loading) {
    return (
      <>
        <AdminNavbar />
        <Container className="text-center mt-5 py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading quick stats...</p>
        </Container>
      </>
    );
  }

  return (
    <>
      <AdminNavbar />
      <Container fluid className="py-4">
        <Row className="mb-4 align-items-center">
          <Col>
            <h2 className="mb-0">Quick Stats Manager</h2>
            <p className="text-muted mb-0">Manage statistics displayed on the home page</p>
          </Col>
          <Col className="text-end">
            <Button 
              variant="primary" 
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
            >
              <i className="fas fa-plus me-2"></i>
              Add New Stat
            </Button>
          </Col>
        </Row>

        {/* Alerts */}
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')} className="mb-4">
            <i className="fas fa-exclamation-circle me-2"></i>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess('')} className="mb-4">
            <i className="fas fa-check-circle me-2"></i>
            {success}
          </Alert>
        )}

        {/* Stats Table */}
        <Card className="border-0 shadow-sm">
          <Card.Header className="bg-light">
            <h5 className="mb-0">Quick Stats ({stats.length})</h5>
            <small className="text-muted">First 4 stats appear on home page. Use arrows to reorder.</small>
          </Card.Header>
          <Card.Body className="p-0">
            {stats.length === 0 ? (
              <div className="text-center py-5">
                <i className="fas fa-chart-bar fa-3x text-muted mb-3"></i>
                <p className="text-muted">No quick stats found</p>
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={() => setShowModal(true)}
                >
                  <i className="fas fa-plus me-1"></i>
                  Add Your First Quick Stat
                </Button>
              </div>
            ) : (
              <Table hover className="mb-0">
                <thead className="bg-light">
                  <tr>
                    <th style={{ width: '50px' }}>Order</th>
                    <th style={{ width: '60px' }}>Icon</th>
                    <th>Title & Value</th>
                    <th>Color</th>
                    <th style={{ width: '100px' }}>Status</th>
                    <th style={{ width: '150px' }} className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.map((stat) => (
                    <tr key={stat.id}>
                      <td>
                        <div className="d-flex flex-column align-items-center">
                          <ButtonGroup size="sm">
                            <Button 
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => moveOrder(stat, 'up')}
                              disabled={stats.findIndex(s => s.id === stat.id) === 0}
                              title="Move Up"
                            >
                              <i className="fas fa-arrow-up"></i>
                            </Button>
                            <Button 
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => moveOrder(stat, 'down')}
                              disabled={stats.findIndex(s => s.id === stat.id) === stats.length - 1}
                              title="Move Down"
                            >
                              <i className="fas fa-arrow-down"></i>
                            </Button>
                          </ButtonGroup>
                          <Badge bg="secondary" className="mt-1">{stat.display_order}</Badge>
                        </div>
                      </td>
                      <td>
                        <div className={`rounded-circle d-inline-flex align-items-center justify-content-center ${colorOptions.find(c => c.value === stat.color)?.class || 'bg-primary'}`}
                             style={{ width: '40px', height: '40px' }}>
                          <i className={`${stat.icon || 'fas fa-chart-line'} text-white`}></i>
                        </div>
                      </td>
                      <td>
                        <div>
                          <strong className="d-block">{stat.title}</strong>
                          <span className="text-primary fw-bold">{stat.value}</span>
                          {stat.link && (
                            <small className="d-block text-muted">
                              <i className="fas fa-link me-1"></i>
                              {stat.link}
                            </small>
                          )}
                        </div>
                      </td>
                      <td>
                        <Badge bg={stat.color} className="text-uppercase">
                          {colorOptions.find(c => c.value === stat.color)?.label || stat.color}
                        </Badge>
                      </td>
                      <td>
                        <Button 
                          variant={stat.is_active ? "success" : "secondary"} 
                          size="sm"
                          onClick={() => toggleStatus(stat)}
                          className="px-3 w-100"
                        >
                          <i className={`fas ${stat.is_active ? 'fa-eye' : 'fa-eye-slash'} me-1`}></i>
                          {stat.is_active ? 'Visible' : 'Hidden'}
                        </Button>
                      </td>
                      <td className="text-end">
                        <Dropdown as={ButtonGroup} size="sm">
                          <Button 
                            variant="outline-primary"
                            onClick={() => handleEdit(stat)}
                            title="Edit"
                          >
                            <i className="fas fa-edit"></i>
                          </Button>
                          <Dropdown.Toggle split variant="outline-primary" />
                          <Dropdown.Menu align="end">
                            <Dropdown.Item onClick={() => handleEdit(stat)}>
                              <i className="fas fa-edit me-2"></i>
                              Edit
                            </Dropdown.Item>
                            <Dropdown.Item onClick={() => toggleStatus(stat)}>
                              <i className={`fas ${stat.is_active ? 'fa-eye-slash' : 'fa-eye'} me-2`}></i>
                              {stat.is_active ? 'Hide' : 'Show'}
                            </Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item 
                              className="text-danger" 
                              onClick={() => handleDeleteClick(stat)}
                            >
                              <i className="fas fa-trash me-2"></i>
                              Delete
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
          {stats.length > 0 && (
            <Card.Footer className="bg-light text-muted">
              <Row className="align-items-center">
                <Col>
                  Showing {stats.length} stat{stats.length !== 1 ? 's' : ''}
                </Col>
                <Col className="text-end">
                  <small>
                    <i className="fas fa-info-circle me-1"></i>
                    Use arrow buttons to reorder stats
                  </small>
                </Col>
              </Row>
            </Card.Footer>
          )}
        </Card>

        {/* Add/Edit Stat Modal */}
        <Modal show={showModal} onHide={handleCloseModal} size="lg" centered backdrop="static">
          <Modal.Header closeButton className="bg-light">
            <Modal.Title>
              <i className={`fas ${editingStat ? 'fa-edit' : 'fa-chart-bar'} me-2`}></i>
              {editingStat ? 'Edit Quick Stat' : 'Add New Quick Stat'}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      <strong>Title</strong> <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control 
                      name="title" 
                      value={formData.title} 
                      onChange={handleInputChange}
                      placeholder="e.g., Total Clients"
                      required
                      className="py-2"
                    />
                    <Form.Text className="text-muted">
                      What this statistic represents
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      <strong>Value</strong> <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control 
                      name="value" 
                      value={formData.value} 
                      onChange={handleInputChange}
                      placeholder="e.g., 400+"
                      required
                      className="py-2"
                    />
                    <Form.Text className="text-muted">
                      The statistic value (can include +, %, etc.)
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Icon</strong></Form.Label>
                    <div className="d-flex flex-wrap gap-2 mb-2">
                      {iconOptions.map((icon, index) => (
                        <Button
                          key={index}
                          type="button"
                          variant={formData.icon === icon ? "primary" : "outline-secondary"}
                          size="sm"
                          onClick={() => setFormData(prev => ({ ...prev, icon }))}
                          title={icon}
                          className="d-flex align-items-center justify-content-center"
                          style={{ width: '40px', height: '40px' }}
                        >
                          <i className={icon}></i>
                        </Button>
                      ))}
                    </div>
                    <InputGroup>
                      <InputGroup.Text>
                        <i className={formData.icon}></i>
                      </InputGroup.Text>
                      <Form.Control 
                        name="icon" 
                        value={formData.icon} 
                        onChange={handleInputChange}
                        placeholder="Font Awesome icon class"
                        className="py-2"
                      />
                    </InputGroup>
                    <Form.Text className="text-muted">
                      Choose from above or enter custom Font Awesome class
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Color</strong></Form.Label>
                    <div className="d-flex flex-wrap gap-2 mb-2">
                      {colorOptions.map((color) => (
                        <Button
                          key={color.value}
                          type="button"
                          variant={formData.color === color.value ? "primary" : "outline-secondary"}
                          size="sm"
                          onClick={() => setFormData(prev => ({ ...prev, color: color.value }))}
                          title={color.label}
                          className={`${color.class} border-0`}
                          style={{ width: '40px', height: '40px' }}
                        >
                          {formData.color === color.value && <i className="fas fa-check"></i>}
                        </Button>
                      ))}
                    </div>
                    <Form.Select 
                      name="color" 
                      value={formData.color} 
                      onChange={handleInputChange}
                      className="py-2"
                    >
                      {colorOptions.map((color) => (
                        <option key={color.value} value={color.value}>
                          {color.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Link (Optional)</strong></Form.Label>
                    <Form.Control 
                      name="link" 
                      value={formData.link} 
                      onChange={handleInputChange}
                      placeholder="e.g., /clients or https://example.com"
                      className="py-2"
                    />
                    <Form.Text className="text-muted">
                      URL to navigate to when clicked (optional)
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Display Order</strong></Form.Label>
                    <Form.Control 
                      type="number" 
                      name="display_order" 
                      value={formData.display_order} 
                      onChange={handleInputChange}
                      min="0"
                      className="py-2"
                    />
                    <Form.Text className="text-muted">
                      Lower numbers appear first (0 = auto)
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              
              <Form.Check
                type="switch"
                id="active-switch"
                label={<strong>Active / Visible</strong>}
                name="is_active"
                checked={Boolean(formData.is_active)}
                onChange={handleInputChange}
                className="mb-3 py-2"
              />
              
              {editingStat && (
                <div className="mt-3 pt-3 border-top">
                  <small className="text-muted">
                    <i className="fas fa-info-circle me-1"></i>
                    Stat ID: {editingStat.id} | Created: {new Date(editingStat.created_at).toLocaleDateString()} | 
                    Last Updated: {new Date(editingStat.updated_at).toLocaleDateString()}
                  </small>
                </div>
              )}
            </Modal.Body>
            <Modal.Footer className="bg-light">
              <Button variant="outline-secondary" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" className="px-4">
                <i className={`fas ${editingStat ? 'fa-save' : 'fa-check'} me-2`}></i>
                {editingStat ? 'Update Stat' : 'Add Stat'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
          <Modal.Header closeButton className="border-0">
            <Modal.Title className="text-danger">
              <i className="fas fa-exclamation-triangle me-2"></i>
              Confirm Deletion
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="py-4">
            {deletingStat && (
              <>
                <div className="text-center mb-4">
                  <div className="rounded-circle bg-danger bg-opacity-10 d-inline-flex align-items-center justify-content-center mb-3" 
                       style={{ width: '80px', height: '80px' }}>
                    <i className="fas fa-chart-bar fa-2x text-danger"></i>
                  </div>
                  <h5>Delete Quick Stat</h5>
                  <p className="text-muted mb-0">
                    Are you sure you want to delete <strong>"{deletingStat.title}"</strong>?
                  </p>
                  <p className="text-danger small mt-2">
                    <i className="fas fa-exclamation-circle me-1"></i>
                    This will remove the statistic from the home page.
                  </p>
                </div>
              </>
            )}
          </Modal.Body>
          <Modal.Footer className="border-0">
            <Button variant="outline-secondary" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete}>
              <i className="fas fa-trash me-2"></i>
              Delete
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </>
  );
};

export default QuickStatsManager;